<template>
  <b-form @keydown="form.errors.clear($event.target.id)" @submit.prevent="onSubmit">
    <div class="row">
      <div class="col-4">
        <b-card header="General">
          <div :class="{ 'invalid-wrapper': form.errors.has('is_company') }" class="form-group row">
            <div class="col-3 col-form-label text-right">Is Company?</div>
            <div class="col-8">
              <core-switch
                v-model="form.is_company"
                color="primary"
                off="N"
                on="Y"
                variant="3d"
                @change="form.errors.clear('is_company')"
              />

              <small
                v-if="form.errors.has('is_company')"
                class="invalid-feedback"
                v-text="form.errors.get('is_company')"
              ></small>
            </div>
          </div>

          <div v-if="form.is_company">
            <form-select
              v-model="form.company_type"
              :options="companyTypes"
              name="company_type"
              @input="form.errors.clear()"
              >Type
            </form-select>
            <form-text v-model="form.company_name" name="company_name" @input="companyNameChanged">
              Name
            </form-text>
            <form-text v-model="form.abn" name="abn">ABN</form-text>

            <div
              :class="{ 'invalid-wrapper': form.errors.has('is_branch') }"
              class="form-group row"
            >
              <div class="col-3 col-form-label text-right">Is Branch?</div>
              <div class="col-8">
                <core-switch
                  v-model="form.is_branch"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                  @change="form.errors.clear('is_branch')"
                />
                <small
                  v-if="form.errors.has('is_branch')"
                  class="invalid-feedback"
                  v-text="form.errors.get('is_branch')"
                ></small>
              </div>
            </div>
            <form-customer-select
              v-if="form.is_branch"
              v-model="form.parent_company"
              error-id="general.parent_company"
              name="parent_company"
              type="companies"
              @change="customerChanged"
            />
          </div>

          <form-select
            v-model="form.payment_terms"
            :options="paymentTerms"
            name="payment_terms"
            @input="form.errors.clear()"
          />

          <form-user-select
            v-model="form.picked_up_by_user"
            :show-extra-slot="true"
            :show-reset-button="true"
            name="picked_up_by"
            placeholder="Search technician..."
            @change="updatePickedUpBy"
          >
            Technician Booking
          </form-user-select>

          <form-text
            :readonly="true"
            :value="$lodash.get(currentPrimaryContact, 'name')"
            name="primary_contact"
          ></form-text>
          <form-text
            :readonly="true"
            :value="$lodash.get(currentPostalAddress, 'shortAddress')"
            name="postal_address"
          ></form-text>
        </b-card>

        <b-card v-if="companyFuzzySearching || companyFuzzySearchResults.length > 0">
          <div v-if="companyFuzzySearching">
            <b-alert class="mb-0" show variant="primary">
              <font-awesome-icon :icon="['fal', 'spinner']" spin />
              Searching similar companies...
            </b-alert>
          </div>
          <div v-else-if="companyFuzzySearchResults.length > 0">
            <div class="pb-2">
              Found {{ companyFuzzySearchTotal }} similar
              {{ companyFuzzySearchResults.length > 1 ? "companies" : "company" }}
            </div>

            <ul class="list-group">
              <li
                v-for="(similarCompany, index) in companyFuzzySearchResults"
                :key="`similar-company-${index}`"
                class="list-group-item px-3 pt-3 pb-2"
              >
                <div class="row">
                  <div class="col d-flex">
                    <customer-object
                      :entity-id="similarCompany.id"
                      :name="similarCompany.company_name"
                      :open-new="true"
                      size="badge-xl"
                    />
                  </div>

                  <div class="col text-right">
                    <b-button
                      v-if="isModal"
                      size="sm"
                      variant="success"
                      @click.prevent="useThisCustomer(similarCompany)"
                    >
                      Use this Customer
                    </b-button>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </b-card>
      </div>
      <div class="col-8">
        <b-card no-body>
          <b-tabs ref="tabs" card>
            <b-tab :title-item-class="errorTabClass('contacts')" title="Contacts">
              <b-card>
                <table class="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Mobile</th>
                      <th style="width: 50px">Primary</th>
                      <th style="width: 50px">Active</th>
                      <th style="width: 50px"></th>
                    </tr>
                  </thead>
                  <tbody v-for="(contact, index) in form.contacts" :key="`contact-${index}`">
                    <tr>
                      <td>
                        <input
                          v-model.trim="contact.first_name"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.first_name`),
                          }"
                          class="form-control"
                          type="text"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.first_name`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.first_name`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.last_name"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.last_name`),
                          }"
                          class="form-control"
                          type="text"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.last_name`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.last_name`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.email"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.email`),
                          }"
                          class="form-control"
                          type="email"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.email`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.email`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.phone"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.phone`),
                          }"
                          class="form-control"
                          type="text"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.phone`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.phone`)"
                        ></small>
                      </td>
                      <td>
                        <input
                          v-model.trim="contact.mobile"
                          :class="{
                            'is-invalid': form && form.errors.has(`contacts.${index}.mobile`),
                          }"
                          class="form-control"
                          type="text"
                          @input="contactsChanged(contact, index)"
                        />
                        <small
                          v-if="form.errors.has(`contacts.${index}.mobile`)"
                          class="invalid-feedback"
                          v-text="form.errors.get(`contacts.${index}.mobile`)"
                        ></small>
                      </td>
                      <td>
                        <core-switch
                          v-if="initialised"
                          :model-checked="contact.is_primary"
                          color="primary"
                          variant="3d"
                          @change="primaryContactChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          v-if="initialised"
                          v-model="contact.is_active"
                          :checked="contact.is_active"
                          color="success"
                          variant="3d"
                          @change="form.errors.clear(`contacts.${index}.is_active`)"
                        />
                      </td>
                      <td style="padding-top: 20px">
                        <font-awesome-icon
                          v-if="contact.is_creating"
                          :icon="['fal', 'times']"
                          class="cursor"
                          @click.stop="removeContact(index)"
                        />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="invalid-wrapper" colspan="8">
                        <small
                          v-if="form.errors.has('contacts.primary_contact_id')"
                          class="invalid-feedback"
                          v-text="form.errors.get('contacts.primary_contact_id')"
                        ></small>
                        <div
                          v-for="(contact, index) in form.contacts"
                          :key="`contact-${index}-error`"
                        >
                          <small
                            v-if="form.errors.has(`contacts.${index}.is_active`)"
                            class="invalid-feedback"
                            v-text="form.errors.get(`contacts.${index}.is_active`)"
                          ></small>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="8">
                        <button
                          class="btn btn-primary btn-sm"
                          type="button"
                          @click.stop="addContact()"
                        >
                          <font-awesome-icon :icon="['fal', 'plus-circle']" />
                          Add Contact
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </b-card>

              <b-card v-if="contactsFuzzySearching || contactsFuzzySearchResults.length > 0">
                <div v-if="contactsFuzzySearching">
                  <b-alert class="mb-0" show variant="primary">
                    <font-awesome-icon :icon="['fal', 'spinner']" spin />
                    Searching similar customers...
                  </b-alert>
                </div>
                <div v-else-if="contactsFuzzySearchResults.length > 0">
                  <div class="pb-2">
                    Found {{ contactsFuzzySearchTotal }} similar
                    {{ contactsFuzzySearchResults.length > 1 ? "customers" : "customer" }}
                  </div>

                  <ul class="list-group" style="max-height: 25vh; overflow-y: auto">
                    <li
                      v-for="(similarCustomer, index) in contactsFuzzySearchResults"
                      :key="`similar-customer-${index}`"
                      class="list-group-item pt-3 pb-2"
                    >
                      <div class="row">
                        <div class="col d-flex">
                          <customer-object
                            :entity-id="similarCustomer.id"
                            :name="getCustomerFullNameTag(similarCustomer)"
                            :open-new="true"
                            size="badge-xl"
                          />
                        </div>
                        <div class="col text-right">
                          <b-button
                            v-if="
                              crud.getAction() === 'edit' && similarCustomer.id === crud.data.id
                            "
                            v-tooltip="{
                              content: 'This contact already exists, for this customer',
                              classes: ['badge-theme'],
                            }"
                            class="mr-1"
                            size="sm"
                            variant="danger"
                          >
                            Current Customer
                          </b-button>

                          <b-button
                            v-if="isModal"
                            size="sm"
                            variant="success"
                            @click.prevent="useThisCustomer(similarCustomer)"
                          >
                            Use this Customer
                          </b-button>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col">
                          <div class="text-muted text-13 pt-1">
                            {{ $lodash.get(similarCustomer, "postal_address.formatted") }}
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </b-card>
            </b-tab>

            <b-tab :title-item-class="errorTabClass('addresses')" title="Addresses">
              <b-card>
                <table v-if="initialised" class="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th style="width: 50px">Postal</th>
                      <th style="width: 50px">Active</th>
                      <th style="width: 50px"></th>
                    </tr>
                  </thead>
                  <tbody v-for="(address, index) in form.addresses" :key="`address-${index}`">
                    <tr>
                      <td>
                        <form-address
                          :address="address"
                          :labelless="true"
                          :name="`addresses.${index}`"
                          :replaceable="true"
                          :single-row="false"
                          :value="address"
                          bottom-margin="mb-0"
                          @updateAddress="addressChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          v-if="initialised"
                          :model-checked="address.is_postal"
                          color="primary"
                          variant="3d"
                          @change="postalAddressChanged($event, index)"
                        />
                      </td>
                      <td>
                        <core-switch
                          v-if="initialised"
                          v-model="address.is_active"
                          :checked="address.is_active"
                          color="success"
                          variant="3d"
                          @change="form.errors.clear(`addresses.${index}.is_active`)"
                        />
                      </td>
                      <td style="padding-top: 20px">
                        <font-awesome-icon
                          v-if="address.is_creating"
                          :icon="['fal', 'times']"
                          class="cursor"
                          @click.stop="removeAddress(index)"
                        />
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="invalid-wrapper" colspan="4">
                        <small
                          v-if="form.errors.has('addresses.postal_address_id')"
                          class="invalid-feedback"
                          v-text="form.errors.get('addresses.postal_address_id')"
                        ></small>

                        <div
                          v-for="(address, index) in form.addresses"
                          :key="`address-${index}-error`"
                        >
                          <small
                            v-if="form.errors.has(`addresses.${index}.is_active`)"
                            class="invalid-feedback"
                            v-text="form.errors.get(`addresses.${index}.is_active`)"
                          ></small>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="4">
                        <button
                          class="btn btn-primary btn-sm"
                          type="button"
                          @click.stop="addAddress()"
                        >
                          <font-awesome-icon :icon="['fal', 'plus-circle']" />
                          Add Address
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </b-card>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>
    </div>

    <form-validation-alert :errors="form.errors" />

    <b-card-footer class="mb-5">
      <button :disabled="form.notReady()" class="btn btn-primary">
        <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fal', 'check']" />
        {{ action === "create" ? "Create" : "Save" }}
      </button>
    </b-card-footer>
  </b-form>
</template>

<script>
import debounce from "debounce";
import ux from "../../../mixins/ux";
import EventBus from "../../../imports/event-bus";

export default {
  mixins: [ux],

  props: {
    customerId: {
      default: null,
    },
    action: {
      default: "create",
    },
  },

  data() {
    return {
      isModal: true,

      companyTypes: [
        "Real Estate",
        "Builder",
        "Contract",
        "Commercial",
        "Retail Business",
        "Hospitality",
        "Health",
      ],
      paymentTerms: ["Cash on Delivery (COD)", "7 Days", "30 Days", "45 Days"],
      initialised: false,
      crud: null,
      form: new window.AppForm({
        is_company: 0,
        is_branch: 0,
        parent_company: "",
        company_name: "",
        company_type: "",
        abn: "",
        payment_terms: "",
        picked_up_by: "",
        picked_up_by_user: "",
        addresses: [],
        contacts: [],
      }),
      contactsFuzzySearching: false,
      contactsFuzzySearchResults: [],
      contactsFuzzySearchTotal: null,
      companyFuzzySearching: false,
      companyFuzzySearchResults: [],
      companyFuzzySearchTotal: null,
    };
  },

  computed: {
    currentPrimaryContact() {
      return this.form.contacts.find((contact) => contact.is_primary);
    },
    currentPostalAddress() {
      return this.form.addresses.find((address) => address.is_postal);
    },
  },

  watch: {
    customerId() {
      this.init();
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    init() {
      this.initialised = false;

      this.crud = new window.AppCrud(
        "customer",
        [
          "primaryContact",
          "branches.primaryContact",
          "branches.postalAddress",
          "branches.pickedUpByUser",
        ],
        this.action
      );

      this.crud.initFormData(this.form, this.customerId).then(() => {
        this.form.is_company = this.form.is_company === 1;

        if (!this.form.payment_terms || this.form.payment_terms.length === 0) {
          this.form.payment_terms = "Cash on Delivery (COD)";
        }
        if (!this.form.company_type || this.form.company_type.length === 0) {
          this.form.company_type = "Real Estate";
        }

        // Initiate if company is branch
        if (this.form.is_company && this.$lodash.has(this.form, "parent_company.id")) {
          this.form.is_branch = true;
        }

        // Initiate contacts with is_primary
        this.form.contacts = this.form.contacts.map((contact) => ({
          ...contact,
          is_primary: contact.id === this.crud.data.primary_contact_id,
          is_active: contact.is_active === 1,
        }));

        // Initiate addresses with is_postal
        this.form.addresses = this.form.addresses.map((address) => ({
          ...address,
          is_postal: address.id === this.crud.data.postal_address_id,
          is_active: address.is_active === 1,
        }));

        // Auto populate default contact/address if it's create action
        if (this.action === "create") {
          this.addContact();
          this.addAddress();
        }

        // Reset fuzzy search
        this.contactsFuzzySearchResults = [];
        this.contactsFuzzySearchTotal = 0;
        this.contactsFuzzySearching = false;

        this.initialised = true;
      });
    },

    onSubmit() {
      this.crud.submit(this.form, this.action, this.customerId).then((response) => {
        if (this.action === "create") {
          this.$emit("created", response);
          window.toastr.success("Customer created!", "Success");
        } else {
          this.$emit("updated", response);
          window.toastr.success("Customer updated!", "Success");
        }
      });
    },

    updatePickedUpBy(user) {
      if (user === null) {
        this.form.picked_up_by_user = null;
        this.form.picked_up_by = null;
      } else {
        this.form.picked_up_by_user = user;
        this.form.picked_up_by = user.id;
      }

      this.form.errors.clear();
    },

    customerChanged(customer) {
      if (!this.form.parent_company || this.form.parent_company.id !== customer.id) {
        // Assign the customer to the newly selected
        this.form.parent_company = customer;
        this.form.parent_company.id = customer.id;

        this.form.errors.clear("general.parent_company");
      }
    },

    getCustomerFullNameTag(customer) {
      if (customer.is_company) {
        return `${customer.primary_contact.name} @ ${customer.company_name} (${customer.company_type})`;
      }

      return customer.primary_contact.name;
    },

    addContact() {
      this.form.contacts.push({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        is_primary: false,
        is_active: true,
        is_creating: true,
      });
    },

    removeContact(index) {
      this.form.contacts.splice(index, 1);

      this.form.errors.clear();
    },

    contactsChanged(contact, index) {
      this.form.errors.clear([
        `contacts.${index}.first_name`,
        `contacts.${index}.last_name`,
        `contacts.${index}.email`,
        `contacts.${index}.phone`,
        `contacts.${index}.mobile`,
      ]);

      if (!contact.id) {
        this.contactFuzzySearch(contact);
      }
    },

    companyNameChanged() {
      this.companyFuzzySearch(this.form.company_name);
    },

    /* eslint-disable func-names */
    companyFuzzySearch: debounce(function (companyName) {
      if (!companyName || companyName.length === 0) {
        return;
      }

      this.companyFuzzySearching = true;

      this.$http
        .get("/customers/company-fuzzy-search", {
          params: {
            company_name: companyName,
          },
        })
        .then((response) => {
          this.companyFuzzySearchResults = this.$lodash.get(response.body, "data", []);
          this.companyFuzzySearchTotal = this.$lodash.get(response.body, "total");

          this.companyFuzzySearching = false;
        });
    }, 700),

    contactFuzzySearch: debounce(function (contact) {
      if (
        this.$lodash.get(contact, "first_name", "").length === 0 &&
        this.$lodash.get(contact, "last_name", "").length === 0 &&
        this.$lodash.get(contact, "email", "").length === 0 &&
        this.$lodash.get(contact, "phone", "").length === 0 &&
        this.$lodash.get(contact, "mobile", "").length === 0
      ) {
        return;
      }

      this.contactsFuzzySearching = true;

      this.$http
        .get("/contacts/fuzzy-search", {
          params: {
            contact,
          },
        })
        .then((response) => {
          this.contactsFuzzySearchResults = this.$lodash.get(response.body, "data", []);
          this.contactsFuzzySearchTotal = this.$lodash.get(response.body, "total");

          this.contactsFuzzySearching = false;
        });
    }, 700),

    useThisCustomer(customer) {
      EventBus.$emit("job-edit-customer-change", customer);
    },

    addAddress() {
      this.form.addresses.push({
        street: "",
        city: "",
        state: "",
        postcode: "",
        latitude: "",
        longitude: "",
        formatted: "",
        is_postal: false,
        is_active: true,
        is_creating: true,
      });
    },

    addressChanged(address, index) {
      this.form.addresses[index] = address;

      this.form.errors.clear();
    },

    removeAddress(index) {
      this.form.addresses.splice(index, 1);

      this.form.errors.clear();
    },

    primaryContactChanged(checked, index) {
      if (checked) {
        this.form.contacts = this.form.contacts.map((contact) => ({
          ...contact,
          is_primary: false,
        }));

        this.form.contacts[index].is_primary = true;
      } else {
        this.form.contacts[index].is_primary = false;
      }

      this.form.errors.clear("contacts.primary_contact_id");
    },

    postalAddressChanged(checked, index) {
      if (checked) {
        this.form.addresses = this.form.addresses.map((address) => ({
          ...address,
          is_postal: false,
        }));

        this.form.addresses[index].is_postal = true;
      } else {
        this.form.addresses[index].is_postal = false;
      }

      this.form.errors.clear("addresses.postal_address_id");
    },

    contactActiveChanged(checked, index) {
      if (!checked) {
        this.form.contacts[index].is_active = !this.form.contacts[index].is_active;
      }

      this.form.errors.clear(`contacts.${index}.is_active`);
    },

    removeBranch(branch, index) {
      window
        .sweetAlert({
          title: "Confirm to remove this branch?",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.$http.put(`/customers/${branch.id}/remove-branch`).then((response) => {
              if (response.status === 200) {
                window.toastr.success(`Branch ${branch.company_name} removed`, "Success");
                this.crud.data.branches.splice(index, 1);
              }
            });
          }
        });
    },
  },
};
</script>
